/**
 * Represents the status of an asynchronous request.
 *
 * @template T - The type of the target identifier or payload used for request tracking.
 */
export interface IRequestStatus<T> {
  requesting: boolean;
  requestingId?: T;
  requestedId?: T;
  error: boolean;
  errorText?: string;
  cancel?: () => void;
}

export interface IAxiosReturnSuccess<T> {
  success: true;
  data: T;
}

export interface IAxiosReturnFail {
  success: false;
  errorText: string;
}

export type IAxiosBlankReturn = IAxiosReturnFail | { success: true };
export type IAxiosReturn<T> = IAxiosReturnSuccess<T> | IAxiosReturnFail;

/**
 * Return shape for validation functions.
 */
export type IValidReturnSuccess = IAxiosReturnSuccess<{
  valid: boolean;
  validationText: string;
  warning?: boolean;
}>;
export type IValidReturn = IAxiosReturnFail | IValidReturnSuccess;

/**
 * Optional context values that validation functions can use alongside the primary target value.
 */
export interface IValidationTargetOptions {
  previousValue?: string;
  existingValues?: string[];
  otherValues?: Record<string, string>;
}

/**
 * Target shape passed to validation functions.
 */
export interface IValidationTarget {
  value: string;
  options?: IValidationTargetOptions;
}

/**
 * Validation function type used by Control components.
 */
export type IValidationFunction = (
  backEndUrl: string,
  target: IValidationTarget,
  controller: AbortController,
) => Promise<IValidReturn>;

/**
 * Configuration for TLF view modes.
 *
 * @param value - The unique identifier for this view mode
 * @param label - The display label for this view mode
 * @param hideForFigures - Optional. When true, hides this mode for figure TLFs
 * @param noRepeatProcessing - Optional. When true, disables repeat processing for this mode
 */
export interface TlfViewModeConfig {
  value: string;
  label: string;
  hideForFigures?: boolean;
  noRepeatProcessing?: boolean;
}

/**
 * Available TLF view mode configurations.
 */
export const TLF_VIEW_MODES: TlfViewModeConfig[] = [
  // The first value will be used as the default
  { value: "TLF", label: "TLF view" },
  { value: "ANNOTATIONS", label: "Annotations view" },
  {
    value: "SUPPRESS_REPEATS",
    label: "Suppress repeats",
    hideForFigures: true,
    noRepeatProcessing: true,
  },
];

export interface EditorProps<T> {
  id: string;
  className?: string;
  decimalAlignPercent?: number;
  editable?: boolean;
  preReplacedText?: T | null;
  resize?: boolean | "vertical";
  setValue: (ret: T) => void;
  style?: React.CSSProperties;
  textAlignment?: "left" | "center" | "right" | "decimal" | "default";
  value: T;
  [key: string]: unknown;
}

export interface EditorDefns<T> {
  Editor: (props: EditorProps<T>) => React.ReactElement;
  blankT: T;
  getTextFromT: (text: T) => string[];
  isT: (value: unknown) => value is T;
  joinTintoBlock: (lines: T[]) => T;
  replaceTextInT: (s: T, oldPhrase: string, newPhrase: T) => T;
  splitTintoLines: (text: T) => T[];
  textToT: (text: string) => T;
}
